import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { reviews } from "@/mocks/reviews";
import usePageMeta from "@/hooks/usePageMeta";
import Highlight from "@/pages/search/components/Highlight";

const typeFilters = [
  { label: "全部结果", value: "all" },
  { label: "电影", value: "movie" },
  { label: "影评", value: "review" },
];

const PAGE_SIZE = 6;

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQ = searchParams.get("q") || "";

  const [input, setInput] = useState(initialQ);
  const [keyword, setKeyword] = useState(initialQ);
  const [type, setType] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: keyword
      ? `“${keyword}”的搜索结果 - 爱的色放电影`
      : "搜索爱情电影与影评 - 爱的色放电影",
    description:
      "在爱的色放电影搜索你感兴趣的爱情电影与影评，支持全文模糊搜索与结果筛选，快速定位想看的内容。",
    keywords: "电影搜索,影评搜索,爱情电影搜索,爱的色放电影",
  });

  const movieResults = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return [];
    return movies.filter((m) =>
      [m.title, m.director, m.region, ...m.genres, ...m.cast, ...m.tags, m.summary]
        .join(" ")
        .toLowerCase()
        .includes(kw)
    );
  }, [keyword]);

  const reviewResults = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return [];
    return reviews.filter((r) =>
      [r.title, r.excerpt, r.author, ...r.content].join(" ").toLowerCase().includes(kw)
    );
  }, [keyword]);

  const totalCount =
    (type === "movie" ? movieResults.length : 0) +
    (type === "review" ? reviewResults.length : 0) +
    (type === "all" ? movieResults.length + reviewResults.length : 0);

  const showMovies = type === "all" || type === "movie";
  const showReviews = type === "all" || type === "review";

  const submit = () => {
    const kw = input.trim();
    setKeyword(kw);
    setVisible(PAGE_SIZE);
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  };

  const hotMovies = [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, 4);

  const visibleMovies = showMovies ? movieResults.slice(0, visible) : [];
  const visibleReviews = showReviews ? reviewResults.slice(0, visible) : [];
  const hasMore =
    (showMovies && movieResults.length > visible) ||
    (showReviews && reviewResults.length > visible);

  return (
    <div className="w-full px-4 md:px-10 pt-28 md:pt-32 pb-12">
      <div className="max-w-[1440px] mx-auto">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">搜索结果</span>
        </nav>

        <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
          搜索影视内容
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600">
          输入电影名称、演员、导演或影评关键词，快速找到你想看的内容
        </p>

        {/* search box */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-3xl">
          <div className="flex-1 flex items-center gap-2 bg-background-50 border border-background-200 rounded-md px-4 py-2.5">
            <i className="ri-search-line text-lg text-foreground-500"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="搜索爱情电影、影评、演员…"
              className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
            />
            {input && (
              <button
                type="button"
                onClick={() => setInput("")}
                aria-label="清空"
                className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={submit}
            className="px-8 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </div>

        {keyword ? (
          <>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                “<span className="text-primary-600 font-medium">{keyword}</span>” 的搜索结果，共{" "}
                <span className="text-primary-600 font-semibold">{totalCount}</span> 条
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                {typeFilters.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => {
                      setType(f.value);
                      setVisible(PAGE_SIZE);
                    }}
                    className={`px-4 py-1.5 rounded-full text-xs cursor-pointer transition-colors whitespace-nowrap ${
                      type === f.value
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {totalCount === 0 ? (
              <div className="mt-10 flex flex-col items-center justify-center py-16 text-center rounded-lg bg-background-50 border border-background-200">
                <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
                <h2 className="mt-4 font-heading font-semibold text-lg text-foreground-900">
                  未找到相关内容
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  换个关键词试试，或看看大家都在看的热门电影
                </p>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 w-full max-w-2xl">
                  {hotMovies.map((m) => (
                    <Link
                      key={m.id}
                      to={`/detail/${m.id}`}
                      className="px-3 py-2.5 rounded-md bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer line-clamp-1"
                    >
                      {m.title}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-8 flex flex-col gap-8">
                {showMovies && movieResults.length > 0 && (
                  <div>
                    <h2 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-950 mb-4">
                      <i className="ri-film-line text-primary-500"></i>
                      电影（{movieResults.length}）
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {visibleMovies.map((m) => (
                        <Link
                          key={m.id}
                          to={`/detail/${m.id}`}
                          className="group flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                        >
                          <div className="w-20 h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
                            <img
                              src={m.poster}
                              alt={`${m.title} 海报`}
                              title={`${m.title} - 搜索结果`}
                              className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <span className="w-fit px-2 py-0.5 rounded bg-secondary-100 text-secondary-900 text-[11px] mb-1.5">
                              电影
                            </span>
                            <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 line-clamp-1">
                              <Highlight text={m.title} keyword={keyword} />
                            </h3>
                            <p className="mt-1 text-xs text-foreground-500">
                              {m.year} · {m.region} · {m.genres.join(" / ")}
                            </p>
                            <p className="mt-1.5 text-xs text-foreground-600 line-clamp-2">
                              <Highlight text={m.summary} keyword={keyword} />
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {showReviews && reviewResults.length > 0 && (
                  <div>
                    <h2 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-950 mb-4">
                      <i className="ri-chat-3-line text-primary-500"></i>
                      影评（{reviewResults.length}）
                    </h2>
                    <div className="flex flex-col gap-4">
                      {visibleReviews.map((r) => (
                        <Link
                          key={r.id}
                          to={`/detail/${r.movieId}`}
                          className="group p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-900 text-[11px]">
                              影评
                            </span>
                            <span className="text-xs text-foreground-500">
                              {r.author} · {r.date}
                            </span>
                          </div>
                          <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 group-hover:text-primary-600 transition-colors">
                            <Highlight text={r.title} keyword={keyword} />
                          </h3>
                          <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                            <Highlight text={r.excerpt} keyword={keyword} />
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {hasMore && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i>
                      加载更多
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="mt-10">
            <h2 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-950 mb-4">
              <i className="ri-fire-line text-primary-500"></i>
              大家都在搜
            </h2>
            <div className="flex flex-wrap gap-2 mb-8">
              {["治愈", "青春校园", "等待", "重逢", "科幻爱情", "经典重温"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setInput(t);
                    setKeyword(t);
                    navigate(`/search?q=${encodeURIComponent(t)}`);
                  }}
                  className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {t}
                </button>
              ))}
            </div>

            <h2 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-950 mb-4">
              <i className="ri-star-line text-primary-500"></i>
              热门电影推荐
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {hotMovies.map((m) => (
                <Link
                  key={m.id}
                  to={`/detail/${m.id}`}
                  className="group rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="w-full aspect-[2/3] overflow-hidden bg-background-200">
                    <img
                      src={m.poster}
                      alt={`${m.title} 海报`}
                      title={`${m.title} - 热门推荐`}
                      className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <p className="font-heading font-semibold text-sm text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {m.title}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">
                      {m.year} · {m.region}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}